import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Box, Button, Grid, Paper, Typography, CircularProgress } from '@mui/material';
import InvoiceDetailDialog from '../components/InvoiceDetailDialog';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import WarningIcon from '@mui/icons-material/Warning';
import { GET_ALL_INVOICES } from '../graphql/query';
import { Invoice, InvoicesData } from '../types/interfaces';
import { PageHeader, StatusBadge } from '../components/common';
import { formatCurrency, getInvoiceStatus } from '../utils/invoiceStatus';

const StatCard: React.FC<{
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, icon, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(90,100,96,0.12)',
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: `${color}15`,
        color: color,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

/** Check if an invoice's status matches one of the given values (case-insensitive). */
function statusIn(invoice: Invoice, ...statuses: string[]): boolean {
  const s = (invoice.status || '').toUpperCase();
  return statuses.some((v) => v.toUpperCase() === s);
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading } = useQuery<InvoicesData>(GET_ALL_INVOICES);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  const invoices = data?.invoices || [];
  const totalRevenue = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  // Use the backend status field for categorization
  const paidAmount = invoices
    .filter((i) => statusIn(i, 'PAID') || (!i.status && i.paid))
    .reduce((sum, inv) => sum + inv.amount, 0);

  const unpaidAmount = invoices
    .filter((i) => statusIn(i, 'DRAFT', 'SENT') || (!i.status && !i.paid))
    .reduce((sum, inv) => sum + inv.amount, 0);

  const overdueAmount = invoices
    .filter((i) => statusIn(i, 'OVERDUE'))
    .reduce((sum, inv) => sum + inv.amount, 0);

  const totalCount = invoices.length;

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-AU');
  };

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={`${totalCount} invoice${totalCount !== 1 ? 's' : ''} total`}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/invoices')}
          >
            New Invoice
          </Button>
        }
      />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Total Revenue"
            value={formatCurrency(totalRevenue)}
            icon={<AttachMoneyIcon />}
            color="#2A9D8F"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Paid"
            value={formatCurrency(paidAmount)}
            icon={<CheckCircleIcon />}
            color="#2E7D32"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Unpaid"
            value={formatCurrency(unpaidAmount)}
            icon={<PendingIcon />}
            color="#E07A3F"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            label="Overdue"
            value={formatCurrency(overdueAmount)}
            icon={<WarningIcon />}
            color="#D32F2F"
          />
        </Grid>
      </Grid>

      {/* Recent invoices */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Recent Invoices
          </Typography>
          <Button size="small" onClick={() => navigate('/invoices')}>
            View All
          </Button>
        </Box>
        {invoices.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No invoices yet. Create your first invoice to get started.
          </Typography>
        ) : (
          invoices.slice(0, 5).map((invoice) => (
            <Box
              key={invoice.id}
              onClick={() => setSelectedInvoiceId(invoice.id)}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 1.5,
                px: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {invoice.customerName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {invoice.invoiceNumber || `#${invoice.id.substring(0, 8).toUpperCase()}`}
                  {invoice.invoiceDate ? ` - ${formatDate(invoice.invoiceDate)}` : ''}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1.5}>
                <StatusBadge status={getInvoiceStatus(invoice)} size="small" />
                <Box textAlign="right">
                  <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                    {formatCurrency(invoice.amount)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Paper>
      <InvoiceDetailDialog
        invoiceId={selectedInvoiceId}
        open={!!selectedInvoiceId}
        onClose={() => setSelectedInvoiceId(null)}
      />
    </>
  );
};

export default DashboardPage;
